import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { API } from "../../services/api";
import { ClassificationLabels } from "../../models/classification.map";

function formatDateTime(value) {
    if (!value) return "Date inconnue";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "Date inconnue";
    return new Intl.DateTimeFormat("fr-FR", {
        dateStyle: "long",
        timeStyle: "short",
    }).format(date);
}

function formatBirthDate(value) {
    if (!value) return null;
    const date = new Date(`${value}T00:00:00`);
    if (Number.isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat("fr-FR").format(date);
}

function productLabel(product) {
    return [product.nom, product.dosage].filter(Boolean).join(" ") || "Produit sans nom";
}

function HistoryEntry({ entry }) {
    const patient = entry.patient || {};
    const products = patient.produits || [];
    const birthDate = formatBirthDate(patient.dateNaissance);
    const classification = patient.classification && patient.classification !== "UNKNOWN"
        ? (ClassificationLabels[patient.classification] || patient.classification)
        : null;

    return (
        <article className="patient-history-entry">
            <header className="patient-history-entry__header">
                <div>
                    <span>Commande #{entry.commandeId}</span>
                    <strong>{formatDateTime(entry.dateReception)}</strong>
                </div>
                <a href={`/?commandeId=${entry.commandeId}`} target="_blank" rel="noopener noreferrer">
                    Ouvrir la commande ↗
                </a>
            </header>
            {entry.objet && <p className="patient-history-entry__subject">{entry.objet}</p>}
            <div className="patient-history-entry__patient">
                <strong>{[patient.nom, patient.prenom].filter(Boolean).join(" ") || "Patient"}</strong>
                {birthDate && <span>Né(e) le {birthDate}</span>}
                {classification && <span>{classification}</span>}
            </div>
            <div className="patient-history-entry__products">
                <span>{products.length} produit{products.length !== 1 ? "s" : ""}</span>
                {products.length === 0 ? (
                    <p>Aucun produit enregistré.</p>
                ) : (
                    <ul>
                        {products.map((product, index) => (
                            <li key={product.id ?? `${product.nom || "produit"}-${index}`}>
                                <strong>{productLabel(product)}</strong>
                                {product.posologie && <span>{product.posologie}</span>}
                                {product.quantite != null && <small>Qté {product.quantite}</small>}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </article>
    );
}

export default function PatientHistoryModal({ patient, onClose }) {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        let active = true;
        setLoading(true);
        setError(false);
        API.get(`/commandes/patients/${patient.id}/history`, { noRedirectOn401: true })
            .then(({ data }) => { if (active) setHistory(Array.isArray(data) ? data.slice(0, 3) : []); })
            .catch(() => { if (active) setError(true); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [patient.id]);

    useEffect(() => {
        const closeOnEscape = event => { if (event.key === "Escape") onClose(); };
        globalThis.addEventListener("keydown", closeOnEscape);
        return () => globalThis.removeEventListener("keydown", closeOnEscape);
    }, [onClose]);

    const patientName = [patient.nom, patient.prenom].filter(Boolean).join(" ") || "ce patient";

    return (
        <div className="patient-history-modal" onMouseDown={event => { if (event.target === event.currentTarget) onClose(); }}>
            <section role="dialog" aria-modal="true" aria-labelledby="patient-history-title" className="patient-history-modal__sheet">
                <header className="patient-history-modal__header">
                    <div>
                        <span>Commandes précédentes</span>
                        <h2 id="patient-history-title">Historique de {patientName}</h2>
                        <p>Les trois dernières commandes dans lesquelles ce patient apparaît.</p>
                    </div>
                    <button type="button" onClick={onClose} aria-label="Fermer l'historique">×</button>
                </header>
                <div className="patient-history-modal__body" aria-live="polite">
                    {loading && <div className="patient-history-modal__state">Chargement de l’historique…</div>}
                    {!loading && error && <div className="patient-history-modal__state patient-history-modal__state--error">L’historique n’a pas pu être chargé.</div>}
                    {!loading && !error && history.length === 0 && <div className="patient-history-modal__state">Aucune commande précédente trouvée.</div>}
                    {!loading && !error && history.map(entry => <HistoryEntry key={entry.commandeId} entry={entry} />)}
                </div>
            </section>
        </div>
    );
}

const productShape = PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    nom: PropTypes.string,
    dosage: PropTypes.string,
    posologie: PropTypes.string,
    quantite: PropTypes.number,
});

HistoryEntry.propTypes = {
    entry: PropTypes.shape({
        commandeId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
        objet: PropTypes.string,
        dateReception: PropTypes.string,
        patient: PropTypes.shape({
            nom: PropTypes.string,
            prenom: PropTypes.string,
            dateNaissance: PropTypes.string,
            classification: PropTypes.string,
            produits: PropTypes.arrayOf(productShape),
        }),
    }).isRequired,
};

PatientHistoryModal.propTypes = {
    patient: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
        nom: PropTypes.string,
        prenom: PropTypes.string,
    }).isRequired,
    onClose: PropTypes.func.isRequired,
};
